import React, { useEffect, useState } from "react";
import { Tab, Typography, Button } from "@mui/material";
import clsx from "clsx";
import { makeStyles } from "@mui/styles";
import { useSelector } from "react-redux";
import axios from "../../MyAxios";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  EDIT_GROUP,
  GET_GROUP,
  GROUP_ADD_MEMBERS,
  GROUP_ADD_RESOURCES,
  LEAVE_GROUP,
  REQUEST_GROUP_JOIN,
  ROOT,
  CHECK_GROUP_DISABLED_FOR_EVENTS,
} from "../../constants/api";
import Layout from "../../Layout";
import back from "../../assets/img/back.svg";
import GroupStreams from "../../components/GroupStreams";
import GroupFilter from "../../components/GroupFilter";
import GroupDescription from "../../components/GroupDescription";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import useLoggedIn from "../../hooks/useLoggedIn";
import AddConnectionIcon from "@images/connectionAdd.png";
import EmailIcon from "@images/emailInfo.png";
import EditGroupIcon from "@images/editInfo.png";
import RemoveIcon from "@images/delete.svg";
import JoinGroupIcon from "@images/joinGroup.svg";
import GroupModal from "@components/GroupModal";
import MyAvatar from "@components/admin/MyAvatar";
import { toast } from "react-toastify";
import TabPane from "@components/TabPane";
import Resources from "./Resources";
import Loader from "@components/Loader";
import { trunc } from "@helpers";
import GroupTagManagement from "@pages/tags/GroupTagManagement";
import GroupEvents from "@pages/event/GroupEvents";

const useStyles = makeStyles((theme) => ({
  textColor: {
    color: theme.palette.primary.main,
  },
  button: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },
  textGrey: {
    color: "#b1b1b1",
  },
  colorGreen: {
    backgroundColor: "#e5f8f1",
  },
  active: {
    color: "#626262",
    borderBottomWidth: "4px",
    borderColor: theme.palette.primary.main,
  },
}));

function GroupInfo() {
  useLoggedIn();
  const navigate = useNavigate();
  const location = useLocation();

  const { userRecord, isLoggedIn } = useSelector((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const classes = useStyles();
  const { id } = useParams();

  // Read any pending tab restore from sessionStorage (set by AddResources / Resources)
  const [active, setActive] = useState(() => {
    const stored = sessionStorage.getItem("groupInfoActiveTab");
    if (stored !== null) {
      sessionStorage.removeItem("groupInfoActiveTab"); // consume immediately
      return Number(stored);
    }
    return 0;
  });

  const [group, setGroup] = useState();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [totalMembers, setTotalMembers] = useState([]);
  const [headerHeight, setHeaderHeight] = useState(0);

  const isAdminOrModerator = (user, group) => {
    return (
      user?.id === group?.admin?.id ||
      group?.moderators?.some((m) => m?.id === user?.id)
    );
  };

  const isAdmin = () => userRecord?.id === group?.admin?.id;

  const options = [];
  const adminOrModerator = isAdminOrModerator(userRecord, group);

  const handleJoinGroup = () => {
    // Prevent joining if community is inactive or user hasn't joined the community
    const communityId = group?.community?.id;
    const communityActive = group?.community?.disabledAt === 0;
    const userIsMemberOfCommunity = Boolean(
      userRecord?.joinedCommunities?.some(
        (c) => String(typeof c === "object" ? c.id : c) === String(communityId),
      ),
    );

    if (!communityActive) {
      toast.error(
        "You cannot join this group because its community is not active",
      );
      return;
    }

    if (!userIsMemberOfCommunity) {
      toast.error(
        "Please join the community before requesting to join this group",
      );
      return;
    }

    axios
      .post(REQUEST_GROUP_JOIN, { fromGroup: group?.id })
      .then(() => {
        toast.success("A join request has been sent successfully");
      })
      .catch(() => {});
  };

  if (adminOrModerator) {
    if (isAdmin() || group?.moderatorAccessLevel?.canAddMembers)
      options.push({
        name: "Add Members from Serkohls",
        img: AddConnectionIcon,
        action: () => {
          navigate(GROUP_ADD_MEMBERS, { state: { groupId: group?.id } });
        },
      });

    options.push({
      name: "Invite by Email",
      img: EmailIcon,
      action: () => {
        navigate(`/group/email-invites/${group?.id}`, {
          state: { groupName: group?.name },
        });
      },
    });

    if (isAdmin() || group?.moderatorAccessLevel?.canEditGroup)
      options.push({
        name: "Edit Group",
        img: EditGroupIcon,
        action: () => {
          navigate(EDIT_GROUP, { state: { group: group } });
        },
      });
  } else if (group?.members?.map((x) => x.id).includes(userRecord?.id)) {
    options.push({
      name: "Leave Group",
      img: RemoveIcon,
      action: () => {
        setOpen(false);
        axios
          .post(LEAVE_GROUP, { groupId: group?.id })
          .then(() => {
            toast.success("Left Group");
          })
          .catch(() => {});
      },
    });
  } else {
    // Only offer the Join Group option when the group's community is active
    // and the user is already a member of that community.
    const communityId = group?.community?.id;
    const communityActive = group?.community?.disabledAt === 0;
    const userIsMemberOfCommunity = Boolean(
      userRecord?.joinedCommunities?.some(
        (c) => String(typeof c === "object" ? c.id : c) === String(communityId),
      ),
    );

    if (communityActive && userIsMemberOfCommunity) {
      options.push({
        name: "Join Group",
        img: JoinGroupIcon,
        action: () => {
          setOpen(false);
          handleJoinGroup();
        },
      });
    }
  }

  const incPage = () => setPage(page + 1);

  const GetGroup = async (onlyGroup = false, noLoading = false) => {
    try {
      if (!noLoading) setLoading(true);

      let route = `${GET_GROUP}/${id}/${page}`;
      if (Boolean(searchParams.get("source")))
        route += `?source=${searchParams.get("source")}`;

      await axios.get(route).then((res) => {
        setGroup(res.data.group);
        setMembers((members) => [...members, ...res.data.group.members]);
        if (!onlyGroup) {
          setTotalMembers(res.data.totalMembers);
          setTotalPages(res.data.totalPages);
        }
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    GetGroup();
  }, [page, id]);

  useEffect(() => {
    if (
      group &&
      userRecord?.allGroups &&
      searchParams.get("trigger") === "join" &&
      !Boolean(userRecord?.allGroups.find((g) => g.id === group.id))
    ) {
      handleJoinGroup();
    }
  }, [group, userRecord, searchParams]);

  useEffect(() => {
    if (!isLoggedIn) {
      let redirectUrl = `/group/info/${id}`;
      if (searchParams.get("trigger") === "join")
        redirectUrl += "?trigger=join";
      localStorage.setItem("loginRedirect", redirectUrl);
    }
  }, [isLoggedIn]);

  const getGroupName = () => {
    if (!group) return "";
    const truncName = trunc(group?.name, 20);
    if (group?.disabledAt > 0) return `${truncName} (Disabled)`;
    return truncName;
  };

  const getCommunityName = (com) => {
    if (!com) return "";
    const truncName = trunc(com?.name, 20);
    if (com?.disabledAt > 0) return `${truncName} (Disabled)`;
    return truncName;
  };

  const [showTopics, setShowTopics] = useState(false);
  const [showEventsFilter, setShowEventsFilter] = useState(false);
  const [postTypeFilter, setPostTypeFilter] = useState("");
  const [groupSelectedTags, setGroupSelectedTags] = useState([]);
  const [groupFromDate, setGroupFromDate] = useState(null);
  const [groupToDate, setGroupToDate] = useState(null);
  const [isGroupDisabledForEvents, setIsGroupDisabledForEvents] =
    useState(false);
  const [eventsDisabledLoaded, setEventsDisabledLoaded] = useState(false); // ✅ NEW

  useEffect(() => {
    if (group?.id) {
      axios
        .get(`${CHECK_GROUP_DISABLED_FOR_EVENTS}/${group.id}`)
        .then((res) => {
          setIsGroupDisabledForEvents(res.data.isDisabled);
        })
        .catch(() => {
          setIsGroupDisabledForEvents(false);
        })
        .finally(() => {
          setEventsDisabledLoaded(true);
        });
    }
  }, [group?.id]);

  const eventsTabLabel = adminOrModerator ? "Add Events" : "View Events";
  const visibleTabs = ["Streams", "Resources", eventsTabLabel, "Group Info"];
  const userIsInMultipleCommunities = userRecord?.joinedCommunities?.length > 1;

  if (loading) return <Loader />;

  return (
    <Layout>
      <div className="w-full bg-white">
        {!showTopics && (
          <div
            ref={(ref) => {
              if (ref) setHeaderHeight(ref.clientHeight);
            }}
            className="fixed w-full z-50"
          >
            <div
              className={clsx(
                classes.colorGreen,
                "shadow-black-100 flex justify-between items-center py-2 shadow-md md:w-1/2 px-2 overflow-y-auto",
              )}
            >
              <div className="flex items-center self-start">
                <img
                  onClick={() => {
                    const fromNotif =
                      Boolean(location?.state?.fromNotifications) ||
                      Boolean(sessionStorage.getItem('fromNotifications'));
                    if (fromNotif) {
                      try {
                        sessionStorage.removeItem('fromNotifications');
                      } catch (e) {}
                      return navigate(ROOT);
                    }
                    return navigate(-1);
                  }}
                  // onClick={() => {
                  //   navigate(ROOT);
                  // }}
                  alt="Back"
                  className="h-4 w-4 mr-4 cursor-pointer"
                  src={back}
                />
                <MyAvatar
                  src={group?.icon?.currBucketUrl}
                  name={
                    <Typography className="inline font-bold">
                      {getGroupName(group?.name)}{" "}
                      {userIsInMultipleCommunities && (
                        <Typography className="text-xs inline">
                          ({getCommunityName(group?.community)})
                        </Typography>
                      )}
                    </Typography>
                  }
                  subtext={`${totalMembers} Members`}
                  textProps={{ className: "font-semibold" }}
                />
              </div>

              {options.length > 0 &&
                group?.disabledAt === 0 &&
                group?.community?.disabledAt === 0 && (
                  <div>
                    <MoreVertIcon
                      className="cursor-pointer"
                      onClick={() => setOpen(true)}
                    />
                  </div>
                )}
            </div>
          </div>
        )}

        <div style={{ marginTop: showTopics ? 0 : headerHeight }}>
          {showTopics ? (
            <div>
              <GroupTagManagement
                group={group}
                getGroup={GetGroup}
                onClose={() => setShowTopics(false)}
                noLayout
                readOnly={!isAdmin()}
              />
            </div>
          ) : (
            <TabPane
              tabs={visibleTabs}
              activeTab={active}
              disabledTabs={
                !eventsDisabledLoaded || isGroupDisabledForEvents ? [2] : []
              }
              onTabChange={(idx) => {
                if (
                  idx === 2 &&
                  (!eventsDisabledLoaded || isGroupDisabledForEvents)
                ) {
                  return;
                }

                if (idx === 2) {
                  if (adminOrModerator) {
                    setActive(0);
                    navigate(`/group/${id}/events/create`, {
                      replace: true,
                      state: {
                        returnTo: window.location.pathname,
                        returnToGroupId: id,
                      },
                    });
                    return;
                  }
                  setActive(1);
                  setShowEventsFilter(false);
                  requestAnimationFrame(() => {
                    setActive(0);
                    setShowEventsFilter(true);
                    setPostTypeFilter("event");
                  });
                  return;
                }
                setShowEventsFilter(false);
                setPostTypeFilter("");
                setActive(idx);
              }}
            >
              {/* Tab 0: Streams */}
              <div>
                <div className="flex justify-end w-full gap-4">
                  <GroupFilter
                    postType={postTypeFilter}
                    setPostType={setPostTypeFilter}
                    group={group}
                    onChange={({
                      selectedTags,
                      fromDate,
                      toDate,
                      postType,
                    }) => {
                      setGroupSelectedTags(selectedTags || []);
                      setGroupFromDate(fromDate || null);
                      setGroupToDate(toDate || null);
                      if (postType !== undefined) setPostTypeFilter(postType);
                    }}
                  />
                </div>
                <GroupStreams
                  group={group}
                  // showOnlyEvents={showEventsFilter}
                  postType={postTypeFilter}
                  selectedTags={groupSelectedTags}
                  fromDate={groupFromDate}
                  toDate={groupToDate}
                />
              </div>

              {/* Tab 1: Resources */}
              <div>
                <Resources
                  groupId={group?.id}
                  allowEdit={adminOrModerator}
                  readOnly={
                    group?.disabledAt > 0 || group?.community?.disabledAt > 0
                  }
                />
              </div>

              {/* Tab 2: Events */}
              <div>
                <GroupEvents />
              </div>

              {/* Tab 3: Group Info */}
              <div>
                <div className="mb-6 px-4">
                  <Typography className="font-semibold">
                    Group Topics
                  </Typography>
                  {(adminOrModerator ||
                    Boolean(
                      group?.members?.some((m) => m?.id === userRecord?.id),
                    )) && (
                    <div className="mt-2">
                      <button
                        onClick={() => setShowTopics(true)}
                        className={clsx(classes.button, "py-2 px-4 rounded-md")}
                      >
                        {isAdmin() ? "Manage Topics >" : "View Topics >"}
                      </button>
                    </div>
                  )}
                </div>
                <GroupDescription
                  group={group}
                  members={members}
                  setGroup={setGroup}
                  setMembers={setMembers}
                  incPage={incPage}
                  page={page}
                  totalPages={totalPages}
                />
              </div>
            </TabPane>
          )}
        </div>
      </div>
      <GroupModal open={open} setOpen={setOpen} options={options} />
    </Layout>
  );
}

export default GroupInfo;
